import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import LogoMark from "@/components/LogoMark";

const LINKS = [
  { to: "/", label: "Accueil" },
  { to: "/le-studio", label: "Le Studio" },
  { to: "/services", label: "Services" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/contact", label: "Contact" },
];

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "border-b border-white/5 bg-noir/80 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-10">
          <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
            <LogoMark size={36} />
            <span className="flex flex-col leading-none">
              <span className="font-serif text-lg tracking-[0.22em] text-ivoire">
                RÉMANENCE
              </span>
              <span className="mt-1 text-[9px] tracking-[0.5em] text-champagne">
                STUDIO
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-9 lg:flex" aria-label="Navigation principale">
            {LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `group relative text-[13px] tracking-[0.18em] uppercase transition-colors duration-300 ${
                    isActive ? "text-champagne" : "text-ivoire/70 hover:text-ivoire"
                  }`
                }
              >
                {link.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-champagne transition-[width] duration-400 group-hover:w-full" />
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-devis-button"
              className="rounded-full border border-champagne/60 px-6 py-2.5 text-[12px] uppercase tracking-[0.2em] text-champagne transition-[background-color,color] duration-400 hover:bg-champagne hover:text-noir"
            >
              Demander un devis
            </Link>
          </nav>

          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="text-ivoire lg:hidden"
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
          >
            {open ? <X size={26} strokeWidth={1} /> : <Menu size={26} strokeWidth={1} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            className="fixed inset-0 z-40 flex flex-col justify-center bg-noir/97 px-10 backdrop-blur-2xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <nav className="flex flex-col gap-2" aria-label="Navigation mobile">
              {LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, y: 28 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.07, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={link.to}
                    data-testid={`nav-mobile-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={({ isActive }) =>
                      `block py-3 font-serif text-4xl font-light ${
                        isActive ? "italic text-champagne" : "text-ivoire"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55 }}
              className="mt-14 text-xs uppercase tracking-[0.3em] text-pierre"
            >
              Créer ce qui demeure — Monteils, Gard
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
